import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader } from '@/acc-ui/primitives/Layout';
import { Tabs } from '@/acc-ui/primitives/Tabs';
import { AccessUsersTab } from './tabs/users-tab';
import { AccessRolesTab } from './tabs/roles-tab';
import { AccessPermissionsTab } from './tabs/permissions-tab';
import { AccessPageRegistryTab } from './tabs/page-registry-tab';
import { accessApi } from '@/api/access-api';
import { usePermission } from '@/auth/use-permission';
import { ACCESS_VIEW_PAGE_REGISTRY } from '@loanaudit/shared';

/**
 * Access Manager — single hub page with four tabs.
 *
 * Why a single hub instead of four top-level pages? The four views are
 * always consulted together: "this user has these roles which include
 * these permissions which appear on these pages." Keeping them
 * co-located reduces cognitive load.
 */

type TabKey = 'users' | 'roles' | 'permissions' | 'pages';

const TAB_KEYS: TabKey[] = ['users', 'roles', 'permissions', 'pages'];

export function AccessManagerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('tab');
  const active: TabKey = TAB_KEYS.includes(param as TabKey) ? (param as TabKey) : 'users';
  const setActive = (k: TabKey) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', k);
      return next;
    });
  const { has } = usePermission();
  const canSeePages = has(ACCESS_VIEW_PAGE_REGISTRY);

  // Fetch counts for the tab badges (cheap queries, refetched on focus)
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: accessApi.listRoles });
  const { data: perms } = useQuery({
    queryKey: ['permissions'],
    queryFn: accessApi.listPermissions,
  });

  const tabs = [
    { key: 'users',       label: 'Users' },
    { key: 'roles',       label: 'Roles',       badge: roles?.length ?? undefined },
    { key: 'permissions', label: 'Permissions', badge: perms?.length ?? undefined },
    ...(canSeePages ? [{ key: 'pages', label: 'Page registry' }] : []),
  ];

  return (
    <AppShell crumb="Admin" title="Access Manager">
      <PageHeader
        title="Access Manager"
        subtitle="Manage users, roles, and permissions."
      />

      <Tabs
        items={tabs}
        active={active}
        onChange={(k) => setActive(k as TabKey)}
      />

      <div className="mt-2">
        {active === 'users' && <AccessUsersTab />}
        {active === 'roles' && <AccessRolesTab />}
        {active === 'permissions' && <AccessPermissionsTab />}
        {active === 'pages' && canSeePages && <AccessPageRegistryTab />}
      </div>
    </AppShell>
  );
}
